import jsPDF from 'jspdf';
import i18n from '../../../i18n';
import { SceneWithUserNames } from '../../../types/scene.types';
import { BreakdownInfo } from '../../../pages/Home/types';
import { registerFont, setFont, SupportedLanguage } from '../PDFExporter';
import { formatDayNightForExport } from '../exportSceneFieldFormat';
import {
  SlotInfo,
  SlotType,
  CalDateInfo,
  ShiftType,
  ScheduleInfo
} from '../../../types/schedule.types';
import { ElementSceneInfo, ElementType, ElementInfo, getRestrictedDatesAsDateStrings } from '../../../types/element.types';
import { buildDisplaySegments } from '../../../utils/calDateSlotsSortUtils';
import { sortCastItemsByNO } from '../../../utils/elementSortUtils';
import { ElementDataCache, ElementRelationDataCache } from '../../../types/cache.types';

// @ts-ignore
const autoTable = require('jspdf-autotable').default;

const t = (key: string, opts?: { lng?: string; [k: string]: unknown }) => i18n.t(key, opts);

const FONT_FILES: Record<SupportedLanguage, { name: string }> = {
  'zh-CN': { name: 'AlibabaPuHuiTi' },
  'zh-HK': { name: 'AlibabaPuHuiTi' },
  en: { name: 'AlibabaPuHuiTi' }
};

const STYLES = {
  PAGE_MARGIN: 9,
  HEADER_FONT: 11,
  WEEK_HEADER_FONT: 11,
  WEEK_HEADER_CELL_PAD: 1.35,
  WEEK_HEADER_MIN_ROW_H: 7.5,
  /** 格内：月-日、班次、中间场次列表 */
  CELL_FONT: 11,
  /** 格内：拍摄天（如 1/8天）、主要角色/受限简称、特·群·页数 */
  CELL_DETAIL_FONT: 11,
  CELL_PAD: 1.2
};

export const PDF_SCHEDULE_CALENDAR_A3_MARGINS_MM = {
  left: STYLES.PAGE_MARGIN,
  right: STYLES.PAGE_MARGIN,
  top: 13,
  bottom: STYLES.PAGE_MARGIN
} as const;

/** 日历单元格内：主演块上、页数行上的分段间距（mm） */
const SECTION_GAP_MM = 1.2;

/** 日期 / 排班 / 拍摄天一行与下方场次列表之间的外沿底间距（mm） */
const HEADER_ROW_BOTTOM_GAP_MM = 2.4;

/** 主要角色区域每行 baseline 间距在字体行高基础上额外加的量（mm） */
const CAST_ROW_LINE_EXTRA_MM = 0.55;

/** 正文与表格线 RGB 分量：纯黑，纸质打印更清晰（原为深灰约 #282828） */
const BODY_TEXT_RGB = 0;
/** 档期冲突角色（与 UI --color-element-conflictDates / #D62D29 一致） */
const CAST_CONFLICT_TEXT_RGB = { r: 214, g: 45, b: 41 } as const;

function applyCastTokenColor(pdf: jsPDF, color: CalendarCastDrawToken['color']) {
  if (color === 'conflict') {
    pdf.setTextColor(
      CAST_CONFLICT_TEXT_RGB.r,
      CAST_CONFLICT_TEXT_RGB.g,
      CAST_CONFLICT_TEXT_RGB.b
    );
    pdf.setDrawColor(
      CAST_CONFLICT_TEXT_RGB.r,
      CAST_CONFLICT_TEXT_RGB.g,
      CAST_CONFLICT_TEXT_RGB.b
    );
  } else {
    pdf.setTextColor(BODY_TEXT_RGB, BODY_TEXT_RGB, BODY_TEXT_RGB);
    pdf.setDrawColor(BODY_TEXT_RGB, BODY_TEXT_RGB, BODY_TEXT_RGB);
  }
}

/** 与 autotable 表头行高度对齐，用于按周分页估算（略保守，避免年份区块错位） */
const CALENDAR_TABLE_HEAD_RESERVE_MM =
  STYLES.WEEK_HEADER_MIN_ROW_H + STYLES.WEEK_HEADER_CELL_PAD * 2 + 0.35;

/** 场号列与日夜列之间的间距（mm） */
const BODY_SCENE_CODE_DN_GAP_MM = 0.9;

/** 日夜列与摘要之间的间距（mm） */
const BODY_SCENE_DN_SUMMARY_GAP_MM = 1.1;

/** 日历格内便签行：正文前加「◎ 」 */
const CALENDAR_NOTE_PREFIX = '◎ ';

/** 拍摄地点下划线线宽（mm），为历史值的一半 */
const LOCATION_UNDERLINE_LINE_WIDTH_MM = 0.275;

/** 场号列固定宽度（mm），全表统一、不做文本测量 */
const BODY_SCENE_CODE_COL_FIXED_MM = 12;

/** 日夜列固定宽度（mm），可容纳最多三个英文字母（如 M、Day、Du、N） */
const BODY_SCENE_DN_COL_FIXED_MM = 8;

/** 场号 + 两空格 + 日夜合并为一块时的最大宽度（mm），与原两列+间距一致 */
const BODY_SCENE_CODE_DN_MERGED_MAX_MM =
  BODY_SCENE_CODE_COL_FIXED_MM + BODY_SCENE_CODE_DN_GAP_MM + BODY_SCENE_DN_COL_FIXED_MM;

/** 合并块内最小字号（pt）；场号+日夜同步缩小到此；仍超宽则截断（无省略号） */
const BODY_SCENE_CODE_FONT_MIN_PT = 9;
const BODY_HEADER_TOP_MARGIN_MM = 1.6;

function lineHeightMm(doc: jsPDF, fontSizePt: number): number {
  const k = doc.internal.scaleFactor;
  const lhf =
    typeof (doc as jsPDF & { getLineHeightFactor?: () => number }).getLineHeightFactor === 'function'
      ? (doc as jsPDF & { getLineHeightFactor: () => number }).getLineHeightFactor()
      : 1.15;
  return (fontSizePt / k) * lhf;
}

function castRowStepMm(doc: jsPDF): number {
  return lineHeightMm(doc, STYLES.CELL_DETAIL_FONT) + CAST_ROW_LINE_EXTRA_MM;
}

function calendarCellNaturalHeightMm(
  doc: jsPDF,
  bodyDrawRows: CalendarBodyDrawRow[],
  castLineCount: number,
  hasFooterRow: boolean
): number {
  const lhBody = lineHeightMm(doc, STYLES.CELL_FONT);
  const lhDetail = lineHeightMm(doc, STYLES.CELL_DETAIL_FONT);
  const castStep = castRowStepMm(doc);
  const padV = STYLES.CELL_PAD * 2;

  let bodyH = 0;
  let hasDrawnBodyRow = false;
  for (const row of bodyDrawRows) {
    if (
      ((row.kind === 'note' && row.isFirstLineOfNote) || row.kind === 'location') &&
      hasDrawnBodyRow
    ) {
      bodyH += BODY_HEADER_TOP_MARGIN_MM;
    }
    bodyH += lhBody;
    hasDrawnBodyRow = true;
  }

  return (
    padV +
    lhBody +
    HEADER_ROW_BOTTOM_GAP_MM +
    bodyH +
    (castLineCount > 0 ? SECTION_GAP_MM + castLineCount * castStep : 0) +
    (hasFooterRow ? SECTION_GAP_MM + lhDetail : 0)
  );
}

export interface CalendarCellCastSegment {
  abbr: string;
  /** 与当天受限主要角色简称相同（场次内+受限交集） */
  conflict: boolean;
}

type CalendarCastDrawToken = {
  text: string;
  color: 'default' | 'conflict';
  bold?: boolean;
  strike?: boolean;
};

export type CalendarCellBodyItem =
  | { kind: 'scene'; code: string; dayNight: string; summary: string }
  | { kind: 'note'; text: string }
  | { kind: 'location'; text: string };

/** 场号 / 日夜 / 摘要列宽固定，摘要自同一竖线起排 */
export type CalendarBodyDrawRow =
  | { kind: 'note'; text: string; isFirstLineOfNote: boolean }
  | { kind: 'location'; text: string; isFirstLineOfLocation: boolean; isLastLineOfLocation: boolean }
  | { kind: 'scene'; code: string; dn: string; summary: string };

export interface CalendarCellLayoutPayload {
  minHeightMm: number;
  language: SupportedLanguage;
  secondaryLanguage?: SupportedLanguage;
  header: { month: number; day: number; shift: string; dayPart: string };
  bodyDrawRows: CalendarBodyDrawRow[];
  /** 超高单元格跨页续打：从第几行开始绘制 */
  bodyStartIndex?: number;
  /** 超高单元格跨页续打：本段最多绘制多少行 */
  bodyMaxRows?: number;
  /** 超高单元格跨页续打：仅首段绘制日期头 */
  drawHeader?: boolean;
  /** 超高单元格跨页续打：仅末段绘制主要角色与底部行 */
  drawCastFooter?: boolean;
  castDrawTokens: CalendarCastDrawToken[];
  castPlainText: string;
  footerLeft: string;
  footerRight: string;
}

function buildCalendarBodyDrawRows(
  doc: jsPDF,
  items: CalendarCellBodyItem[],
  innerW: number,
  fontName: string,
  fontSizePt: number,
  language: SupportedLanguage,
  secondaryLanguage: SupportedLanguage | undefined,
  maxLines: number
): CalendarBodyDrawRow[] {
  const rows: CalendarBodyDrawRow[] = [];
  setFont(doc, language, 'normal');
  doc.setFontSize(fontSizePt);
  doc.setFont(fontName, 'normal');

  const mergedMaxW = BODY_SCENE_CODE_DN_MERGED_MAX_MM;
  const reserved = mergedMaxW + BODY_SCENE_DN_SUMMARY_GAP_MM;
  const summaryW = Math.max(8, innerW - reserved);

  for (const it of items) {
    if (rows.length >= maxLines) break;
    if (it.kind === 'note') {
      const t = (it.text || '').replace(/\r\n/g, '\n').trim();
      if (!t) continue;
      const noteLines = wrapCalendarNoteToDrawRows(doc, t, innerW, fontName, fontSizePt, language);
      for (const nl of noteLines) {
        if (rows.length >= maxLines) break;
        rows.push({ kind: 'note', text: nl.text, isFirstLineOfNote: nl.isFirstLineOfNote });
      }
      continue;
    } else if (it.kind === 'location') {
      const t = (it.text || '').trim();
      if (!t) continue;
      const locationLines = wrapTextToMaxLinesNoEllipsis(doc, t, innerW, fontName, fontSizePt, 2);
      for (let li = 0; li < locationLines.length; li++) {
        if (rows.length >= maxLines) break;
        rows.push({
          kind: 'location',
          text: locationLines[li] || '',
          isFirstLineOfLocation: li === 0,
          isLastLineOfLocation: li === locationLines.length - 1,
        });
      }
    } else {
      const code = (it.code || '').trim();
      const dn = formatDayNightForExport(language, it.dayNight, secondaryLanguage);
      const summary = (it.summary || '').trim();
      const summaryTwoLines = summary
        ? wrapTextToMaxLinesNoEllipsis(doc, summary, summaryW, fontName, fontSizePt, 2)
        : [];
      const firstSummaryLine = summaryTwoLines[0] || '';
      rows.push({ kind: 'scene', code, dn, summary: firstSummaryLine });
      if (summaryTwoLines.length > 1 && rows.length < maxLines) {
        rows.push({ kind: 'scene', code: '', dn: '', summary: summaryTwoLines[1] || '' });
      }
    }
  }

  return rows;
}

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYmd(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatCalendarYearLabelForWeeks(
  weekIndices: number[],
  weeks: Date[][],
  exportRange: { start: string; end: string }
): string {
  const years = new Set<number>();
  for (const wi of weekIndices) {
    const week = weeks[wi];
    if (!week) continue;
    for (const d of week) {
      const ds = toYmd(d);
      if (ds >= exportRange.start && ds <= exportRange.end) {
        years.add(d.getFullYear());
      }
    }
  }
  const sorted = Array.from(years).sort((a, b) => a - b);
  return sorted.join(' / ');
}

function shiftLabel(shift: string | undefined, lng: SupportedLanguage): string {
  if (!shift) return '';
  const map: Record<string, string> = {
    MORNING: t('schedule.shiftMorning', { lng }),
    AFTERNOON: t('schedule.shiftAfternoon', { lng }),
    EVENING: t('schedule.shiftEvening', { lng }),
    APPENDIX1: t('schedule.shiftAppendix1', { lng }),
    APPENDIX2: t('schedule.shiftAppendix2', { lng }),
    APPENDIX3: t('schedule.shiftAppendix3', { lng }),
    APPENDIX4: t('schedule.shiftAppendix4', { lng }),
    APPENDIX5: t('schedule.shiftAppendix5', { lng })
  };
  return map[shift] ?? shift;
}

function getShootingSortedDates(scheduleSolts: SlotInfo[]): string[] | null {
  const slotTypeRows =
    scheduleSolts?.filter((pt) => pt.slotType === SlotType.SHOOT || !pt.slotType) ?? [];
  if (slotTypeRows.length === 0) return null;
  const validDateStrings = slotTypeRows
    .map((pt) => pt.slotDate)
    .filter((day): day is string => !!day)
    .map((day) => day.split('T')[0])
    .filter((ds) => !isNaN(parseYmd(ds).getTime()));
  if (validDateStrings.length === 0) return null;
  return Array.from(new Set(validDateStrings)).sort();
}

function shootingDayNumber(dateStr: string, sortedDates: string[] | null): number | null {
  if (!sortedDates?.length) return null;
  const i = sortedDates.indexOf(dateStr);
  return i >= 0 ? i + 1 : null;
}

function buildRestrictedCastIdsByDate(elements: ElementInfo[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const item of elements) {
    const id = item.elementID;
    if (!id || item.elementType !== ElementType.CAST) continue;
    const dates = getRestrictedDatesAsDateStrings(item.elementRestrictedDates);
    for (const dateStr of dates) {
      if (!dateStr || dateStr.length < 10) continue;
      let set = map.get(dateStr);
      if (!set) {
        set = new Set<string>();
        map.set(dateStr, set);
      }
      set.add(id);
    }
  }
  return map;
}

function groupedSoltsForCalendarDay(
  dateStr: string,
  daySolts: SlotInfo[],
  dayDateRecord: CalDateInfo | undefined,
  scenes: SceneWithUserNames[],
  options?: { forceShowAllLocations?: boolean }
): Array<{
  type: 'slotGroup' | 'note';
  items: SlotInfo[];
  shift?: ShiftType;
  showLocationHeader?: boolean;
  locationLabel?: string;
}> {
  const slotMap = new Map<string, SlotInfo>();
  daySolts.forEach((slot) => slotMap.set(slot.slotID, slot));
  const getSceneInfo = (sceneID: string | undefined) =>
    sceneID ? scenes.find((s) => s.sceneID === sceneID) : undefined;

  const segments = buildDisplaySegments(
    daySolts,
    dayDateRecord?.calDateSlotsSort,
    dateStr,
    (sid) => {
      const s = getSceneInfo(sid);
      return s ? { sceneLocation: s.sceneLocation } : undefined;
    },
    { forceShowAllLocations: options?.forceShowAllLocations }
  );
  const result: Array<{
    type: 'slotGroup' | 'note';
    items: SlotInfo[];
    shift?: ShiftType;
    showLocationHeader?: boolean;
    locationLabel?: string;
  }> = [];

  for (const seg of segments) {
    if (seg.kind === 'note') {
      const slots = seg.items
        .map((id) => slotMap.get(id))
        .filter((x): x is SlotInfo => !!x);
      if (slots.length === 0) continue;
      result.push({ type: 'note', items: slots });
    } else {
      const slots = seg.items
        .map((id) => slotMap.get(id))
        .filter((x): x is SlotInfo => !!x);
      if (slots.length === 0) continue;
      result.push({
        type: 'slotGroup',
        items: slots,
        shift: seg.shift,
        showLocationHeader: seg.showHeader,
        locationLabel: seg.locationLabel
      });
    }
  }

  return result;
}

function formatPagesTotal(n: number, lng: SupportedLanguage): string {
  const s = n.toFixed(1).replace(/\.0$/, '');
  return `${s} ${t('schedule.pages', { lng })}`;
}

/** 仅场号：在 [minPt, maxPt] 内从大到小取能放入 availableW 的字号；minPt 仍超宽则截断（无省略号） */
function fitSceneCodeForAvailableWidth(
  doc: jsPDF,
  language: SupportedLanguage,
  fontName: string,
  code: string,
  availableW: number,
  maxPt: number,
  minPt: number
): { fontPt: number; drawText: string } {
  if (!code) return { fontPt: maxPt, drawText: '' };
  setFont(doc, language, 'normal');
  doc.setFont(fontName, 'normal');
  for (let pt = maxPt; pt >= minPt; pt -= 0.5) {
    doc.setFontSize(pt);
    if (doc.getTextWidth(code) <= availableW) {
      return { fontPt: pt, drawText: code };
    }
  }
  doc.setFontSize(minPt);
  return {
    fontPt: minPt,
    drawText: truncateNoEllipsis(doc, code, availableW, fontName, minPt)
  };
}

/** 场号+两空格+日夜：整段同步缩小至不低于 minCodePt；仍超宽则在最小字号直接截断（无省略号） */
function drawSceneCodeDnMerged(
  pdf: jsPDF,
  language: SupportedLanguage,
  fontName: string,
  xL: number,
  yBody: number,
  mergedMaxW: number,
  fsDnPt: number,
  minCodePt: number,
  codeRaw: string,
  dnRaw: string
) {
  const code = (codeRaw || '').trim();
  const dn = (dnRaw || '').trim();
  if (!code && !dn) return;

  setFont(pdf, language, 'normal');
  pdf.setFont(fontName, 'normal');
  pdf.setTextColor(BODY_TEXT_RGB, BODY_TEXT_RGB, BODY_TEXT_RGB);

  if (!code) {
    pdf.setFontSize(fsDnPt);
    const draw = truncateNoEllipsis(pdf, dn, mergedMaxW, fontName, fsDnPt);
    if (draw) pdf.text(draw, xL, yBody);
    return;
  }

  if (!dn) {
    const { fontPt, drawText } = fitSceneCodeForAvailableWidth(
      pdf,
      language,
      fontName,
      code,
      mergedMaxW,
      fsDnPt,
      minCodePt
    );
    pdf.setFontSize(fontPt);
    pdf.text(drawText, xL, yBody);
    return;
  }

  const mergedText = `${code}  ${dn}`;
  for (let pt = fsDnPt; pt >= minCodePt; pt -= 0.5) {
    pdf.setFontSize(pt);
    if (pdf.getTextWidth(mergedText) <= mergedMaxW) {
      pdf.text(mergedText, xL, yBody);
      return;
    }
  }
  pdf.setFontSize(minCodePt);
  const drawMerged = truncateNoEllipsis(pdf, mergedText, mergedMaxW, fontName, minCodePt);
  if (drawMerged) pdf.text(drawMerged, xL, yBody);
}

function truncateNoEllipsis(doc: jsPDF, text: string, maxW: number, fontName: string, fontSize: number): string {
  doc.setFontSize(fontSize);
  doc.setFont(fontName, 'normal');
  if (doc.getTextWidth(text) <= maxW) return text;
  let lo = 0;
  let hi = text.length;
  let best = '';
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const sub = text.slice(0, mid);
    if (doc.getTextWidth(sub) <= maxW) {
      best = sub;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

function wrapTextToMaxLinesNoEllipsis(
  doc: jsPDF,
  raw: string,
  maxW: number,
  fontName: string,
  fontSizePt: number,
  maxLines: number
): string[] {
  if (maxW <= 0 || maxLines <= 0) return [];
  const normalized = raw.replace(/\r\n/g, '\n').trim();
  if (!normalized) return [];
  doc.setFontSize(fontSizePt);
  doc.setFont(fontName, 'normal');
  const wrapped = doc.splitTextToSize(normalized, maxW) as string[];
  if (wrapped.length <= maxLines) return wrapped;
  const lines = wrapped.slice(0, maxLines);
  lines[maxLines - 1] = truncateNoEllipsis(doc, lines[maxLines - 1] || '', maxW, fontName, fontSizePt);
  return lines;
}

/** 便签多行：按 inner 宽度折行；首行带「◎ 」，续行与首行正文左缘对齐 */
function wrapCalendarNoteToDrawRows(
  doc: jsPDF,
  raw: string,
  innerW: number,
  fontName: string,
  fontSizePt: number,
  language: SupportedLanguage
): { text: string; isFirstLineOfNote: boolean }[] {
  setFont(doc, language, 'normal');
  doc.setFontSize(fontSizePt);
  doc.setFont(fontName, 'normal');
  const prefixW = doc.getTextWidth(CALENDAR_NOTE_PREFIX);
  const contentW = Math.max(0, innerW - prefixW);
  if (contentW <= 0) return [];
  const normalized = raw.replace(/\r\n/g, '\n').trim();
  if (!normalized) return [];

  // 便签导出不限制两行：按可用宽度自然折行，最终可绘制行数由外层版面分页逻辑控制
  const chunks = doc.splitTextToSize(normalized, contentW) as string[];

  return chunks.map((text, i) => ({
    text,
    isFirstLineOfNote: i === 0,
  }));
}

function measureCastTokenLineCount(
  pdf: jsPDF,
  tokens: CalendarCastDrawToken[],
  maxW: number,
  fontName: string,
  fontSize: number,
  language: SupportedLanguage
): number {
  if (tokens.length === 0) return 0;
  const tokenWidth = (tk: CalendarCastDrawToken): number => {
    const fontStyle = tk.bold ? 'bold' : 'normal';
    setFont(pdf, language, fontStyle);
    pdf.setFontSize(fontSize);
    pdf.setFont(fontName, fontStyle);
    const txt = tk.text || '';
    return txt ? pdf.getTextWidth(txt) : 0;
  };

  let lines = 1;
  let x = 0;
  for (const tk of tokens) {
    let w = tokenWidth(tk);
    if (x > 0 && x + w > maxW) {
      lines += 1;
      x = 0;
    }
    if (w > maxW && x === 0) {
      const text = truncateNoEllipsis(pdf, tk.text, maxW, fontName, fontSize);
      w = text ? tokenWidth({ ...tk, text }) : 0;
    }
    x += w;
  }
  return lines;
}

function buildCastDrawTokens(
  mainSegments: CalendarCellCastSegment[],
  restrictedAbbrs: string[]
): CalendarCastDrawToken[] {
  const tokens: CalendarCastDrawToken[] = [];
  mainSegments.forEach((seg, i) => {
    if (i > 0) tokens.push({ text: '/', color: 'default' });
    tokens.push({
      text: seg.abbr,
      color: seg.conflict ? 'conflict' : 'default',
      bold: seg.conflict
    });
  });

  if (restrictedAbbrs.length > 0) {
    if (tokens.length > 0) tokens.push({ text: ' ', color: 'default' });
    tokens.push({ text: '[', color: 'conflict' });
    restrictedAbbrs.forEach((abbr, i) => {
      if (i > 0) tokens.push({ text: '/', color: 'conflict' });
      tokens.push({ text: abbr, color: 'conflict', strike: true });
    });
    tokens.push({ text: ']', color: 'conflict' });
  }

  return tokens;
}

function buildCastPlainText(tokens: CalendarCastDrawToken[]): string {
  return tokens.map((t) => t.text).join('');
}

function drawCalendarCastArea(
  pdf: jsPDF,
  xLeft: number,
  innerW: number,
  yFirstLine: number,
  lineStep: number,
  fs: number,
  language: SupportedLanguage,
  fontName: string,
  tokens: CalendarCastDrawToken[]
) {
  if (tokens.length === 0) return;
  const strikeOffset = 1.05;
  const strikeWidth = 0.22;
  const xMax = xLeft + innerW;
  let x = xLeft;
  let y = yFirstLine;

  for (const tk of tokens) {
    const fontStyle = tk.bold ? 'bold' : 'normal';
    setFont(pdf, language, fontStyle);
    pdf.setFontSize(fs);
    pdf.setFont(fontName, fontStyle);

    let drawText = tk.text;
    let w = pdf.getTextWidth(drawText);
    if (x > xLeft && x + w > xMax) {
      y += lineStep;
      x = xLeft;
    }
    if (w > innerW && x === xLeft) {
      drawText = truncateNoEllipsis(pdf, drawText, innerW, fontName, fs);
      w = pdf.getTextWidth(drawText);
    }
    if (!drawText) continue;

    applyCastTokenColor(pdf, tk.color);
    pdf.text(drawText, x, y);
    if (tk.strike && w > 0) {
      pdf.setLineWidth(strikeWidth);
      pdf.line(x, y - strikeOffset, x + w, y - strikeOffset);
    }
    x += w;
  }

  pdf.setTextColor(BODY_TEXT_RGB, BODY_TEXT_RGB, BODY_TEXT_RGB);
  pdf.setDrawColor(BODY_TEXT_RGB, BODY_TEXT_RGB, BODY_TEXT_RGB);
}

function buildCalendarCellModel(
  dateStr: string,
  date: Date,
  calendarDays: CalDateInfo[],
  scheduleSolts: SlotInfo[],
  scenes: SceneWithUserNames[],
  elementRelations: ElementSceneInfo[],
  elementById: Map<string, ElementInfo>,
  shootingSorted: string[] | null,
  restrictedByDate: Map<string, Set<string>>,
  includeNoScheduleCast: boolean,
  forceShowAllLocations: boolean,
  lng: SupportedLanguage
): {
  header: { month: number; day: number; shift: string; dayPart: string };
  bodyItems: CalendarCellBodyItem[];
  castMainSegments: CalendarCellCastSegment[];
  castRestrictedAbbrs: string[];
  footerL: string;
  footerR: string;
  hasFooter: boolean;
} {
  const dayDateRecord = calendarDays.find((pd) => {
    if (!pd.calDateDt) return false;
    return pd.calDateDt.split('T')[0] === dateStr;
  });

  const daySolts = scheduleSolts.filter((pt) => {
    if (!pt.slotDate) return false;
    return pt.slotDate.split('T')[0] === dateStr;
  });

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const shift = shiftLabel(dayDateRecord?.calDateShift, lng);
  const dayN = shootingDayNumber(dateStr, shootingSorted);
  const total = shootingSorted?.length ?? 0;
  const dayPart =
    dayN != null && total > 0
      ? t('export.calendarShootingDayLabel', { lng, current: dayN, total })
      : '';

  const grouped = groupedSoltsForCalendarDay(dateStr, daySolts, dayDateRecord, scenes, {
    forceShowAllLocations
  });
  const getScene = (id?: string) => (id ? scenes.find((s) => s.sceneID === id) : undefined);

  const bodyItems: CalendarCellBodyItem[] = [];
  for (const g of grouped) {
    if (g.type === 'slotGroup' && g.showLocationHeader) {
      const slot = g.items[0];
      const scene = getScene(slot?.slotBelongSceneID);
      const locationFromScene = (scene?.sceneLocation || '').trim();
      const locationFromGroup = (g.locationLabel || '').trim();
      const location = locationFromScene || locationFromGroup || t('schedule.locationNotSet', { lng });
      bodyItems.push({ kind: 'location', text: location });
    }

    for (const slot of g.items) {
      if (g.type === 'note' || slot.slotType === SlotType.NOTE) {
        const line = (slot.slotName || '').replace(/\r\n/g, '\n').trim();
        if (line) bodyItems.push({ kind: 'note', text: line });
      } else {
        const sc = getScene(slot.slotBelongSceneID);
        const code = (sc?.sceneCode || slot.slotSceneCode || '').trim();
        const dayNight = (sc?.sceneDayNight ?? '') as string;
        const summary = (sc?.scenePlotSummary || '').trim();
        if (code || summary || dayNight) {
          bodyItems.push({ kind: 'scene', code, dayNight, summary });
        }
      }
    }
  }

  const dayShootSolts = daySolts.filter((x) => x.slotType === SlotType.SHOOT || !x.slotType);
  const sceneIDs = dayShootSolts.map((x) => x.slotBelongSceneID).filter((id): id is string => !!id);
  const sceneIDSet = new Set(sceneIDs);
  // 关系表历史上可能不存 elementType / 简称，类型与简称均以 elements 为准
  const castItemIds = new Set<string>();
  elementRelations.forEach((rel) => {
    if (!rel.elementID || !sceneIDSet.has(rel.sceneID)) return;
    const item = elementById.get(rel.elementID);
    if (!item || item.elementType !== ElementType.CAST) return;
    castItemIds.add(rel.elementID);
  });
  const castItemsFromElements: ElementSceneInfo[] = [];
  castItemIds.forEach((id) => {
    const item = elementById.get(id);
    if (!item) return;
    castItemsFromElements.push({
      elementID: id,
      sceneID: '',
      elementType: item.elementType,
      elementName: item.elementName,
      elementAbbr: item.elementAbbr,
      elementNO: item.elementNO
    });
  });
  const castSorted = sortCastItemsByNO(castItemsFromElements);

  const restrictedIds = restrictedByDate.get(dateStr) ?? new Set<string>();
  const restrictedItemsForDay: ElementInfo[] = [];
  restrictedIds.forEach((id) => {
    const item = elementById.get(id);
    if (item && item.elementType === ElementType.CAST) restrictedItemsForDay.push(item);
  });
  const restrictedSorted = sortCastItemsByNO(
    restrictedItemsForDay.map((item) => ({
      elementID: item.elementID!,
      sceneID: '',
      elementType: item.elementType,
      elementName: item.elementName,
      elementAbbr: item.elementAbbr,
      elementNO: item.elementNO
    }))
  );
  const restrictedAbbrSet = new Set<string>();
  if (includeNoScheduleCast) {
    restrictedSorted.forEach((r) => {
      const a = (r.elementAbbr || '').trim();
      if (a) restrictedAbbrSet.add(a);
    });
  }

  const castMainSegments: CalendarCellCastSegment[] = [];
  castSorted.forEach((r) => {
    const abbr = (r.elementAbbr || '').trim();
    if (!abbr) return;
    const conflict = restrictedAbbrSet.has(abbr);
    castMainSegments.push({ abbr, conflict });
  });

  /** 右侧为当日「全部」受限主要角色简称（与场次重叠的也在此列出；重叠者在左侧另有冲突底纹） */
  const castRestrictedAbbrs = includeNoScheduleCast
    ? restrictedSorted
        .map((r) => (r.elementAbbr || '').trim())
        .filter((abbr): abbr is string => !!abbr)
    : [];

  const featuredRoleRelations = elementRelations.filter((rel) => {
    if (!rel.elementID || !sceneIDSet.has(rel.sceneID)) return false;
    return elementById.get(rel.elementID)?.elementType === ElementType.FEATURED;
  });
  const maxFeat = new Map<string, number>();
  featuredRoleRelations.forEach((rel) => {
    if (!rel.elementID) return;
    const q = rel.elementQtyInScene || 1;
    maxFeat.set(rel.elementID, Math.max(maxFeat.get(rel.elementID) || 0, q));
  });
  let featured = 0;
  maxFeat.forEach((v) => {
    featured += v;
  });

  const crowdRels = elementRelations.filter((rel) => {
    if (!rel.elementID || !sceneIDSet.has(rel.sceneID)) return false;
    return elementById.get(rel.elementID)?.elementType === ElementType.BACKGROUND;
  });
  const maxCrowd = new Map<string, number>();
  crowdRels.forEach((rel) => {
    if (!rel.elementID) return;
    const q = rel.elementQtyInScene || 1;
    maxCrowd.set(rel.elementID, Math.max(maxCrowd.get(rel.elementID) || 0, q));
  });
  let crowd = 0;
  maxCrowd.forEach((v) => {
    crowd += v;
  });

  const stuntRels = elementRelations.filter((rel) => {
    if (!rel.elementID || !sceneIDSet.has(rel.sceneID)) return false;
    return elementById.get(rel.elementID)?.elementType === ElementType.STUNT_PLAYERS;
  });
  const maxStunt = new Map<string, number>();
  stuntRels.forEach((rel) => {
    if (!rel.elementID) return;
    const q = rel.elementQtyInScene || 1;
    maxStunt.set(rel.elementID, Math.max(maxStunt.get(rel.elementID) || 0, q));
  });
  let stunt = 0;
  maxStunt.forEach((v) => {
    stunt += v;
  });

  const uniqueSceneIDs = Array.from(new Set(sceneIDs));
  let pages = 0;
  uniqueSceneIDs.forEach((sid) => {
    const sc = getScene(sid);
    if (sc?.scenePagesNumber != null) {
      const n = Number(sc.scenePagesNumber);
      if (!isNaN(n)) pages += n;
    }
  });

  const pipe = t('common.pipeSeparator', { lng });
  const footerL =
    `${t('schedule.featuredShort', { lng })} ${featured}${pipe}${t('schedule.crowdShort', { lng })} ${crowd}` +
    (stunt > 0 ? `${pipe}${t('schedule.stuntShort', { lng })} ${stunt}` : '');
  const footerR = formatPagesTotal(pages, lng);

  return {
    header: { month, day, shift, dayPart },
    bodyItems,
    castMainSegments,
    castRestrictedAbbrs,
    footerL,
    footerR,
    hasFooter: dayShootSolts.length > 0
  };
}

type CalendarCellForDraw = {
  x: number;
  y: number;
  width: number;
  height: number;
  padding: (side: 'top' | 'bottom' | 'left' | 'right' | 'vertical' | 'horizontal') => number;
};

type CalendarTableCellRaw = {
  content: string;
  _rowHeightMm?: number;
  _calendarLayout?: CalendarCellLayoutPayload;
};

type CalendarWeekRowModel = {
  weekIndex: number;
  rowCells: CalendarTableCellRaw[];
  naturalHeightMm: number;
  hasCalendarCell: boolean;
};

type CalendarRenderedRow = {
  kind: 'week' | 'continuation';
  weekIndex: number;
  heightMm: number;
  /** 超高周行跨页续打：第几段（0-based） */
  segmentIndex: number;
};

function distributeRemainingHeightEvenly(targetHeights: number[], pageHeightMm: number): number[] {
  if (targetHeights.length === 0) return [];
  const sum = targetHeights.reduce((a, b) => a + b, 0);
  const remaining = pageHeightMm - sum;
  if (remaining <= 0) return targetHeights.slice();
  const add = remaining / targetHeights.length;
  const out = targetHeights.map((h) => h + add);
  const outSum = out.reduce((a, b) => a + b, 0);
  const diff = pageHeightMm - outSum;
  out[out.length - 1] += diff;
  return out;
}

function computeCalendarCellBodySegment(
  pdf: jsPDF,
  bodyDrawRows: CalendarBodyDrawRow[],
  startIndex: number,
  cellHeightMm: number,
  hasFooterRow: boolean,
  castDrawTokens: CalendarCastDrawToken[],
  drawHeader: boolean,
  innerW: number,
  fontName: string,
  language: SupportedLanguage
): { startIndex: number; maxRows: number } {
  const fsBody = STYLES.CELL_FONT;
  const fsDetail = STYLES.CELL_DETAIL_FONT;
  const k = pdf.internal.scaleFactor;
  const lhBody = lineHeightMm(pdf, fsBody);
  const lhDetail = lineHeightMm(pdf, fsDetail);
  const padTop = STYLES.CELL_PAD;
  const padBottom = STYLES.CELL_PAD;

  const yTop = padTop;
  const innerBottom = cellHeightMm - padBottom;
  const yHeader = yTop + (fsBody / k) * (2 - 1.15);
  const yBodyStart = drawHeader ? yHeader + lhBody + HEADER_ROW_BOTTOM_GAP_MM : yTop + lhBody * 0.8;

  const footerBaseline = innerBottom - lhDetail * 0.28;
  const castBlockBottom = footerBaseline - SECTION_GAP_MM;

  const castStep = castRowStepMm(pdf);
  const castLineCount = measureCastTokenLineCount(
    pdf,
    castDrawTokens,
    innerW,
    fontName,
    fsDetail,
    language
  );
  const castLastBaseline = castLineCount > 0 ? castBlockBottom - castStep : castBlockBottom;
  const castFirstBaseline = castLineCount > 0 ? castLastBaseline - (castLineCount - 1) * castStep : castBlockBottom;

  // 若该日期不画 footer，则 body 可以占用到 cell 底部（减去一点缓冲以避免与边框/基线碰撞）
  const bodyMaxBaseline = hasFooterRow
    ? (castLineCount > 0 ? castFirstBaseline - SECTION_GAP_MM - lhBody : footerBaseline - SECTION_GAP_MM - lhBody)
    : innerBottom - lhBody * 0.2;

  let yBody = yBodyStart;
  let hasDrawnBodyRow = false;
  let maxRows = 0;
  for (let i = startIndex; i < bodyDrawRows.length; i++) {
    const row = bodyDrawRows[i]!;
    if (
      ((row.kind === 'note' && row.isFirstLineOfNote) || (row.kind === 'location' && row.isFirstLineOfLocation)) &&
      hasDrawnBodyRow
    ) {
      yBody += BODY_HEADER_TOP_MARGIN_MM;
    }
    if (yBody > bodyMaxBaseline) break;
    maxRows += 1;
    yBody += lhBody;
    hasDrawnBodyRow = true;
  }
  return { startIndex, maxRows };
}

function paginateCalendarWeekRowsForRender(
  weekRows: CalendarWeekRowModel[],
  pageBodyHeightMm: number,
  minRowHeightMm: number
): CalendarRenderedRow[][] {
  if (pageBodyHeightMm <= 0) {
    return weekRows.map((w) => [
      { kind: 'week', weekIndex: w.weekIndex, heightMm: w.naturalHeightMm, segmentIndex: 0 }
    ]);
  }

  const pages: CalendarRenderedRow[][] = [];
  let cur: CalendarRenderedRow[] = [];
  let curHeights: number[] = [];
  let used = 0;

  const flush = () => {
    if (cur.length === 0) return;
    const adjusted = distributeRemainingHeightEvenly(curHeights, pageBodyHeightMm);
    const out = cur.map((r, i) => ({ ...r, heightMm: adjusted[i] }));
    pages.push(out);
    cur = [];
    curHeights = [];
    used = 0;
  };

  for (const w of weekRows) {
    if (!w.hasCalendarCell) continue;
    const h = w.naturalHeightMm;

    // 超高行：跨页续打（按段绘制 body rows），并且该行独占页面
    if (h > pageBodyHeightMm) {
      flush();
      const pageCount = Math.max(1, Math.ceil(h / pageBodyHeightMm));
      // 前 N-1 段各占满整页（无法与其他行共页）
      for (let i = 0; i < Math.max(0, pageCount - 1); i++) {
        pages.push([
          {
            kind: i === 0 ? 'week' : 'continuation',
            weekIndex: w.weekIndex,
            heightMm: pageBodyHeightMm,
            segmentIndex: i
          }
        ]);
      }

      // 最后一段不强制占满整页：按剩余高度占位（且不小于最小行高），允许同页继续排后续周行
      const remaining = h - (pageCount - 1) * pageBodyHeightMm;
      const lastH = Math.max(minRowHeightMm, Math.min(pageBodyHeightMm, remaining > 0 ? remaining : pageBodyHeightMm));
      cur.push({
        kind: pageCount === 1 ? 'week' : 'continuation',
        weekIndex: w.weekIndex,
        heightMm: lastH,
        segmentIndex: pageCount - 1
      });
      curHeights.push(lastH);
      used = lastH;
      continue;
    }

    if (cur.length === 0) {
      cur.push({ kind: 'week', weekIndex: w.weekIndex, heightMm: h, segmentIndex: 0 });
      curHeights.push(h);
      used = h;
      continue;
    }

    if (used + h <= pageBodyHeightMm) {
      cur.push({ kind: 'week', weekIndex: w.weekIndex, heightMm: h, segmentIndex: 0 });
      curHeights.push(h);
      used += h;
    } else {
      flush();
      cur.push({ kind: 'week', weekIndex: w.weekIndex, heightMm: h, segmentIndex: 0 });
      curHeights.push(h);
      used = h;
    }
  }

  flush();
  return pages.length ? pages : [[]];
}

function drawCalendarCellContent(
  pdf: jsPDF,
  cell: CalendarCellForDraw,
  payload: Omit<CalendarCellLayoutPayload, 'minHeightMm'>
) {
  const fsBody = STYLES.CELL_FONT;
  const fsDetail = STYLES.CELL_DETAIL_FONT;
  pdf.setFontSize(fsBody);
  const lhBody = lineHeightMm(pdf, fsBody);
  const lhDetail = lineHeightMm(pdf, fsDetail);
  const k = pdf.internal.scaleFactor;
  const xL = cell.x + cell.padding('left');
  const xR = cell.x + cell.width - cell.padding('right');
  const innerW = cell.width - cell.padding('horizontal');
  const yTop = cell.y + cell.padding('top');
  const innerBottom = cell.y + cell.height - cell.padding('bottom');
  const { language } = payload;
  const fontName = FONT_FILES[language].name;

  const yHeader = yTop + (fsBody / k) * (2 - 1.15);
  const { month, day, shift, dayPart } = payload.header;

  const drawHeader = payload.drawHeader !== false;
  if (drawHeader) {
    pdf.setTextColor(BODY_TEXT_RGB, BODY_TEXT_RGB, BODY_TEXT_RGB);
    setFont(pdf, language, 'normal');
    pdf.setFont(fontName, 'normal');
    const dateLabel = `${month}-${day}`;
    pdf.text(dateLabel, xL, yHeader);
    const leftBlockW = pdf.getTextWidth(dateLabel);
    let rightBlockW = 0;
    if (dayPart) {
      pdf.setFontSize(fsDetail);
      rightBlockW = pdf.getTextWidth(dayPart);
      pdf.setFontSize(fsBody);
    }
    const gapSide = 2;
    const centerBudget = Math.max(8, innerW - leftBlockW - rightBlockW - gapSide * 2);
    let shiftDraw = shift;
    if (shiftDraw) {
      if (pdf.getTextWidth(shiftDraw) > centerBudget) {
        shiftDraw = truncateNoEllipsis(pdf, shiftDraw, centerBudget, fontName, fsBody);
      }
      const cx = cell.x + cell.width / 2;
      pdf.text(shiftDraw, cx, yHeader, { align: 'center' });
    }
    if (dayPart) {
      pdf.setFontSize(fsDetail);
      setFont(pdf, language, 'normal');
      pdf.text(dayPart, xR, yHeader, { align: 'right' });
      pdf.setFontSize(fsBody);
    }
  }

  const castStep = castRowStepMm(pdf);
  const footerBaseline = innerBottom - lhDetail * 0.28;
  const castBlockBottom = footerBaseline - SECTION_GAP_MM;
  setFont(pdf, language, 'normal');
  pdf.setFontSize(fsDetail);
  pdf.setFont(fontName, 'normal');
  const drawCastFooter = payload.drawCastFooter !== false;
  const castTokens = drawCastFooter ? payload.castDrawTokens : [];
  const castLineCount = drawCastFooter
    ? measureCastTokenLineCount(pdf, castTokens, innerW, fontName, fsDetail, language)
    : 0;
  const castLastBaseline =
    castLineCount > 0 ? castBlockBottom - castStep : castBlockBottom;
  const castFirstBaseline =
    castLineCount > 0 ? castLastBaseline - (castLineCount - 1) * castStep : castBlockBottom;
  const hasFooterRow = drawCastFooter && Boolean(payload.footerLeft || payload.footerRight);
  const bodyMaxBaseline = hasFooterRow
    ? (castLineCount > 0 ? castFirstBaseline - SECTION_GAP_MM - lhBody : footerBaseline - SECTION_GAP_MM - lhBody)
    : innerBottom - lhBody * 0.2;

  let yBody = drawHeader ? yHeader + lhBody + HEADER_ROW_BOTTOM_GAP_MM : yTop + lhBody * 0.8;
  let hasDrawnBodyRow = false;
  const startIdx = Math.max(0, payload.bodyStartIndex ?? 0);
  const maxRows = payload.bodyMaxRows != null ? Math.max(0, payload.bodyMaxRows) : Infinity;
  let drawn = 0;
  // 手写单元格内容前统一正文色，避免与其它绘制步骤遗留的 doc 状态不一致
  pdf.setTextColor(BODY_TEXT_RGB, BODY_TEXT_RGB, BODY_TEXT_RGB);
  for (let i = startIdx; i < payload.bodyDrawRows.length; i++) {
    if (drawn >= maxRows) break;
    const row = payload.bodyDrawRows[i]!;
    if (
      ((row.kind === 'note' && row.isFirstLineOfNote) || (row.kind === 'location' && row.isFirstLineOfLocation)) &&
      hasDrawnBodyRow
    ) {
      yBody += BODY_HEADER_TOP_MARGIN_MM;
    }
    if (yBody > bodyMaxBaseline) break;
    if (row.kind === 'note') {
      setFont(pdf, language, 'normal');
      pdf.setFontSize(fsBody);
      pdf.setFont(fontName, 'normal');
      const prefixW = pdf.getTextWidth(CALENDAR_NOTE_PREFIX);
      const xText = xL + prefixW;
      if (row.isFirstLineOfNote) {
        pdf.text(CALENDAR_NOTE_PREFIX, xL, yBody);
        if (row.text) pdf.text(row.text, xText, yBody);
      } else if (row.text) {
        pdf.text(row.text, xText, yBody);
      }
    } else if (row.kind === 'location') {
      setFont(pdf, language, 'bold');
      pdf.setFont(fontName, 'bold');
      pdf.text(row.text, xL + innerW / 2, yBody, { align: 'center' });
      const w = pdf.getTextWidth(row.text);
      const xStart = xL + innerW / 2 - w / 2;
      const underlineY = yBody + 0.45;
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(LOCATION_UNDERLINE_LINE_WIDTH_MM);
      pdf.line(xStart, underlineY, xStart + w, underlineY);
      pdf.setLineWidth(0.2);
      pdf.setDrawColor(BODY_TEXT_RGB, BODY_TEXT_RGB, BODY_TEXT_RGB);
      setFont(pdf, language, 'normal');
      pdf.setFont(fontName, 'normal');
    } else {
      const mergedMaxW = BODY_SCENE_CODE_DN_MERGED_MAX_MM;
      const xSum = xL + mergedMaxW + BODY_SCENE_DN_SUMMARY_GAP_MM;
      drawSceneCodeDnMerged(
        pdf,
        language,
        fontName,
        xL,
        yBody,
        mergedMaxW,
        fsBody,
        BODY_SCENE_CODE_FONT_MIN_PT,
        row.code,
        row.dn
      );
      pdf.setFontSize(fsBody);
      setFont(pdf, language, 'normal');
      pdf.setFont(fontName, 'normal');
      if (row.summary) {
        const summaryMaxW = Math.max(0, xR - xSum);
        const sumDraw =
          summaryMaxW > 0
            ? truncateNoEllipsis(pdf, row.summary, summaryMaxW, fontName, fsBody)
            : '';
        if (sumDraw) pdf.text(sumDraw, xSum, yBody);
      }
    }
    hasDrawnBodyRow = true;
    yBody += lhBody;
    drawn += 1;
  }

  if (drawCastFooter) {
    drawCalendarCastArea(
      pdf,
      xL,
      innerW,
      castFirstBaseline,
      castStep,
      fsDetail,
      language,
      fontName,
      castTokens
    );
  }

  pdf.setTextColor(BODY_TEXT_RGB, BODY_TEXT_RGB, BODY_TEXT_RGB);
  pdf.setFontSize(fsDetail);
  if (drawCastFooter && (payload.footerLeft || payload.footerRight)) {
    pdf.text(payload.footerLeft, xL, footerBaseline);
    pdf.text(payload.footerRight, xR, footerBaseline, { align: 'right' });
  }
  pdf.setFontSize(fsBody);
}

async function initCalendarDoc(
  doc: jsPDF,
  language: SupportedLanguage
): Promise<{ doc: jsPDF; fontName: string }> {
  doc = new jsPDF('l', 'mm', 'a3');
  const fontRegistered = await registerFont(doc, language);
  if (!fontRegistered) {
    console.warn(`字体注册失败，将使用默认字体。语言: ${language}`);
  }
  setFont(doc, language);
  const fontName = fontRegistered ? FONT_FILES[language].name : 'helvetica';
  return { doc, fontName };
}

/** 拍摄计划开机日期，只取 scheduleStartDate 的日期部分 */
function formatScheduleStartDate(value: string | null | undefined): string {
  if (!value) return '';
  const day = String(value).split('T')[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : '';
}

/** 制作日历：页眉（每页表格开始前绘制，见 autotable willDrawPage） */
function drawScheduleCalendarDocHeader(
  doc: jsPDF,
  language: SupportedLanguage,
  fontName: string,
  scheduleInfo: ScheduleInfo | null,
  breakdownInfo: BreakdownInfo,
  yearYearsText: string
) {
  const pageW = doc.internal.pageSize.getWidth();
  const m = STYLES.PAGE_MARGIN;
  const y = STYLES.PAGE_MARGIN;
  const fs = STYLES.HEADER_FONT;
  doc.setFontSize(fs);
  setFont(doc, language, 'normal');
  doc.setFont(fontName, 'normal');

  const filmName =
    typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') || '' : '';
  const scheduleVersionText = (scheduleInfo?.scheduleName || '').trim();
  const scriptVer = (breakdownInfo?.breakdownScriptOverview || '').trim();
  const startStr = formatScheduleStartDate(scheduleInfo?.scheduleStartDate);
  const lng = language;

  const leftRaw = t('export.scheduleCalendarHeaderLeft', { lng, film: filmName, version: scheduleVersionText });
  const scriptRaw = t('export.scheduleCalendarHeaderScriptLine', {
    lng,
    label: t('export.scriptVersion', { lng }),
    value: scriptVer
  });
  const startRaw = t('export.scheduleCalendarHeaderStartLine', {
    lng,
    label: t('export.scheduleCalendarStartDateLabel', { lng }),
    value: startStr
  });

  const leftMax = Math.max(24, pageW * 0.3 - m);
  const scriptMax = Math.max(28, pageW * 0.22);
  const yearMax = Math.max(22, pageW * 0.14);
  const startMax = Math.max(28, pageW * 0.2);

  const leftText = truncateNoEllipsis(doc, leftRaw, leftMax, fontName, fs);
  const scriptText = truncateNoEllipsis(doc, scriptRaw, scriptMax, fontName, fs);
  const yearText = truncateNoEllipsis(doc, (yearYearsText || '').trim(), yearMax, fontName, fs);
  const startText = truncateNoEllipsis(doc, startRaw, startMax, fontName, fs);

  doc.text(leftText, m, y);

  const xMidL = pageW * 0.38;
  doc.text(scriptText, xMidL, y, { align: 'center' });

  const xMidR = pageW * 0.58;
  doc.text(yearText, xMidR, y, { align: 'center' });

  doc.text(startText, pageW - m, y, { align: 'right' });
}

function enumerateWeeks(rangeStart: string, rangeEnd: string): Date[][] {
  const s = parseYmd(rangeStart);
  const e = parseYmd(rangeEnd);
  s.setHours(12, 0, 0, 0);
  e.setHours(12, 0, 0, 0);
  const firstSun = new Date(s);
  firstSun.setDate(s.getDate() - s.getDay());
  const weeks: Date[][] = [];
  let weekStart = new Date(firstSun);
  while (weekStart <= e) {
    const row: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      row.push(d);
    }
    const rowStart = row[0];
    const rowEnd = row[6];
    if (rowEnd >= s && rowStart <= e) {
      weeks.push(row);
    }
    weekStart.setDate(weekStart.getDate() + 7);
    if (weeks.length > 120) break;
  }
  return weeks;
}

export interface ExportScheduleCalendarParams {
  language: SupportedLanguage;
  secondaryLanguage?: SupportedLanguage;
  scenes: SceneWithUserNames[];
  breakdownInfo: BreakdownInfo;
  breakdownId?: string;
  scheduleInfo: ScheduleInfo | null;
  scheduleSolts: SlotInfo[];
  calendarDays: CalDateInfo[];
  exportRange: { start: string; end: string };
  includeNoScheduleCast: boolean;
  /** 强制所有 SHOOT 分组都显示地点头（等同“打印拍摄地点=所有”） */
  forceShowAllLocations?: boolean;
  optionName?: string;
  getElementCache?: (breakdownId: string) => ElementDataCache | null;
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null;
  onProgress?: (n: number) => void;
}

export async function exportScheduleCalendarDocument(
  initialDoc: jsPDF,
  params: ExportScheduleCalendarParams
): Promise<jsPDF> {
  const {
    language,
    secondaryLanguage,
    scenes,
    breakdownInfo,
    breakdownId,
    scheduleInfo,
    scheduleSolts,
    calendarDays,
    exportRange,
    includeNoScheduleCast,
    forceShowAllLocations = false,
    getElementCache,
    getElementRelationCache,
    onProgress
  } = params;

  const { doc, fontName } = await initCalendarDoc(initialDoc, language);
  onProgress?.(15);

  let elementRelations: ElementSceneInfo[] = [];
  if (breakdownId && getElementRelationCache) {
    elementRelations = getElementRelationCache(breakdownId)?.elementRelations || [];
  }

  const elements: ElementInfo[] =
    breakdownId && getElementCache ? getElementCache(breakdownId)?.elements || [] : [];
  const elementById = new Map<string, ElementInfo>();
  elements.forEach((item) => {
    if (item.elementID) elementById.set(item.elementID, item);
  });
  const restrictedByDate = buildRestrictedCastIdsByDate(elements);
  const shootingSorted = getShootingSortedDates(scheduleSolts);

  const weeks = enumerateWeeks(exportRange.start, exportRange.end);
  const lng = language;

  const weekdayLabels = [
    t('schedule.weekdaySun', { lng }),
    t('schedule.weekdayMon', { lng }),
    t('schedule.weekdayTue', { lng }),
    t('schedule.weekdayWed', { lng }),
    t('schedule.weekdayThu', { lng }),
    t('schedule.weekdayFri', { lng }),
    t('schedule.weekdaySat', { lng })
  ];

  const pageW = doc.internal.pageSize.getWidth();
  const margin = STYLES.PAGE_MARGIN;
  const contentW = pageW - margin * 2;
  const colW = contentW / 7;
  const tableTop = PDF_SCHEDULE_CALENDAR_A3_MARGINS_MM.top;
  const pageHeightMm = doc.internal.pageSize.getHeight();

  doc.setFont(fontName, 'normal');
  doc.setFontSize(STYLES.CELL_FONT);
  const innerW = colW - STYLES.CELL_PAD * 2;

  const headReserveMm = CALENDAR_TABLE_HEAD_RESERVE_MM;
  const pageBodyHeightMm = pageHeightMm - tableTop - margin - headReserveMm;
  const minRowMm = pageBodyHeightMm > 0 ? pageBodyHeightMm / 5 : 0;

  const weekRows: CalendarWeekRowModel[] = [];
  for (let weekIndex = 0; weekIndex < weeks.length; weekIndex++) {
    const week = weeks[weekIndex]!;
    let maxH = 0;
    let hasCalendarCell = false;
    const rowCells: CalendarTableCellRaw[] = [];

    for (const d of week) {
      const ds = toYmd(d);
      if (ds < exportRange.start || ds > exportRange.end) {
        rowCells.push({ content: '' });
        continue;
      }
      hasCalendarCell = true;
      const model = buildCalendarCellModel(
        ds,
        d,
        calendarDays,
        scheduleSolts,
        scenes,
        elementRelations,
        elementById,
        shootingSorted,
        restrictedByDate,
        includeNoScheduleCast,
        forceShowAllLocations,
        lng
      );
      const bodyDrawRows = buildCalendarBodyDrawRows(
        doc,
        model.bodyItems,
        innerW,
        fontName,
        STYLES.CELL_FONT,
        language,
        secondaryLanguage,
        200
      );
      const castDrawTokens = buildCastDrawTokens(model.castMainSegments, model.castRestrictedAbbrs);
      setFont(doc, language, 'normal');
      doc.setFontSize(STYLES.CELL_DETAIL_FONT);
      doc.setFont(fontName, 'normal');
      const castPlainText = buildCastPlainText(castDrawTokens);
      const castLineCount = measureCastTokenLineCount(
        doc,
        castDrawTokens,
        innerW,
        fontName,
        STYLES.CELL_DETAIL_FONT,
        language
      );
      const minHeightMm = calendarCellNaturalHeightMm(doc, bodyDrawRows, castLineCount, model.hasFooter);
      maxH = Math.max(maxH, minHeightMm);
      const payload: CalendarCellLayoutPayload = {
        minHeightMm,
        language,
        secondaryLanguage,
        header: model.header,
        bodyDrawRows,
        castDrawTokens,
        castPlainText,
        footerLeft: model.hasFooter ? model.footerL : '',
        footerRight: model.hasFooter ? model.footerR : ''
      };
      rowCells.push({ content: '\u00a0', _calendarLayout: payload });
    }

    const naturalHeightMm = hasCalendarCell ? Math.max(maxH, minRowMm) : minRowMm;
    weekRows.push({ weekIndex, rowCells, naturalHeightMm, hasCalendarCell });
  }

  const pages = paginateCalendarWeekRowsForRender(weekRows, pageBodyHeightMm, minRowMm);
  const maxSegmentIndexByWeek = new Map<number, number>();
  pages.forEach((rows) => {
    rows.forEach((r) => {
      maxSegmentIndexByWeek.set(r.weekIndex, Math.max(maxSegmentIndexByWeek.get(r.weekIndex) ?? 0, r.segmentIndex));
    });
  });
  const weekIndicesByPdfPage: number[][] = pages.map((rows) =>
    Array.from(new Set(rows.filter((r) => r.segmentIndex === 0).map((r) => r.weekIndex)))
  );

  onProgress?.(25);

  const baseTableOptions = {
    head: [weekdayLabels],
    startY: tableTop,
    theme: 'grid' as const,
    margin: { left: margin, right: margin, top: tableTop, bottom: margin },
    tableWidth: contentW,
    styles: {
      font: fontName,
      fontSize: STYLES.CELL_FONT,
      cellPadding: STYLES.CELL_PAD,
      valign: 'top' as const,
      overflow: 'linebreak' as const,
      lineWidth: 0.2,
      lineColor: BODY_TEXT_RGB,
      textColor: BODY_TEXT_RGB
    },
    headStyles: {
      font: fontName,
      fillColor: [240, 240, 240] as any,
      textColor: 0,
      fontStyle: 'bold' as const,
      fontSize: STYLES.WEEK_HEADER_FONT,
      halign: 'center' as const,
      valign: 'middle' as const,
      cellPadding: STYLES.WEEK_HEADER_CELL_PAD,
      minCellHeight: STYLES.WEEK_HEADER_MIN_ROW_H,
      // grid 主题默认 head.lineWidth 为 0，表头无格线；与表体一致画出内外边框
      lineWidth: 0.2,
      lineColor: BODY_TEXT_RGB
    },
    columnStyles: {
      0: { cellWidth: colW },
      1: { cellWidth: colW },
      2: { cellWidth: colW },
      3: { cellWidth: colW },
      4: { cellWidth: colW },
      5: { cellWidth: colW },
      6: { cellWidth: colW }
    }
  };

  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    if (pageIdx > 0) doc.addPage();
    const renderedRows = pages[pageIdx] ?? [];
    const weekIdx = weekIndicesByPdfPage[pageIdx] ?? [];
    const yearLabel = formatCalendarYearLabelForWeeks(weekIdx, weeks, exportRange);

    const body: CalendarTableCellRaw[][] = renderedRows.map((rr) => {
      const weekRow = weekRows[rr.weekIndex];
      const base = weekRow ? weekRow.rowCells : null;
      const out: CalendarTableCellRaw[] = [];
      for (let i = 0; i < 7; i++) {
        const src = base?.[i];
        if (src && src._calendarLayout) {
          const layout = src._calendarLayout;
          const segInnerW = colW - STYLES.CELL_PAD * 2;
          const lastSeg = maxSegmentIndexByWeek.get(rr.weekIndex) ?? 0;
          const isFirstSeg = rr.segmentIndex === 0;
          const isLastSeg = rr.segmentIndex >= lastSeg;

          let startIndex = 0;
          for (let si = 0; si < rr.segmentIndex; si++) {
            const seg = computeCalendarCellBodySegment(
              doc,
              layout.bodyDrawRows,
              startIndex,
              rr.heightMm,
              false,
              [],
              si === 0,
              segInnerW,
              FONT_FILES[layout.language].name,
              layout.language
            );
            startIndex += seg.maxRows;
            if (seg.maxRows <= 0) break;
          }
          const seg = computeCalendarCellBodySegment(
            doc,
            layout.bodyDrawRows,
            startIndex,
            rr.heightMm,
            isLastSeg && Boolean(layout.footerLeft || layout.footerRight),
            isLastSeg ? layout.castDrawTokens : [],
            isFirstSeg,
            segInnerW,
            FONT_FILES[layout.language].name,
            layout.language
          );
          const segLayout: CalendarCellLayoutPayload = {
            ...layout,
            bodyStartIndex: seg.startIndex,
            bodyMaxRows: seg.maxRows,
            drawHeader: isFirstSeg,
            drawCastFooter: isLastSeg,
            castDrawTokens: isLastSeg ? layout.castDrawTokens : [],
            castPlainText: isLastSeg ? layout.castPlainText : '',
            footerLeft: isLastSeg ? layout.footerLeft : '',
            footerRight: isLastSeg ? layout.footerRight : ''
          };
          out.push({ content: src.content, _calendarLayout: segLayout, _rowHeightMm: rr.heightMm });
        } else if (src) {
          out.push({ content: src.content, _rowHeightMm: rr.heightMm });
        } else {
          out.push({ content: '', _rowHeightMm: rr.heightMm });
        }
      }
      return out;
    });

    autoTable(doc, {
      ...baseTableOptions,
      body,
      didParseCell: (data: {
        section: string;
        cell: { raw: unknown; styles: { overflow?: unknown; minCellHeight?: number } };
      }) => {
        if (data.section !== 'body') return;
        const raw = data.cell.raw;
        if (!raw || typeof raw !== 'object') return;

        if ('_rowHeightMm' in raw && typeof (raw as CalendarTableCellRaw)._rowHeightMm === 'number') {
          data.cell.styles.minCellHeight = (raw as CalendarTableCellRaw)._rowHeightMm;
        }
        if ('_calendarLayout' in raw && (raw as CalendarTableCellRaw)._calendarLayout) {
          data.cell.styles.overflow = () => [''];
        }
      },
      didDrawCell: (data: { section: string; cell: CalendarCellForDraw & { raw: unknown }; doc: jsPDF }) => {
        if (data.section !== 'body') return;
        const raw = data.cell.raw;
        if (!raw || typeof raw !== 'object' || !('_calendarLayout' in raw)) return;
        const layout = (raw as CalendarTableCellRaw)._calendarLayout;
        if (!layout) return;
        const { minHeightMm: _mh, ...drawPayload } = layout;
        void _mh;
        drawCalendarCellContent(data.doc, data.cell, drawPayload);
      },
      willDrawPage: () => {
        drawScheduleCalendarDocHeader(doc, language, fontName, scheduleInfo, breakdownInfo, yearLabel);
      }
    });
  }

  onProgress?.(90);
  return doc;
}
